import type { ParseKeys } from "i18next";
import React from "react";
import { StatusBubble } from "./StatusBubble";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";

type BubbleType =
  | "preorder"
  | "fulfilled"
  | "paid"
  | "partial"
  | "unpaid"
  | "voided";

type BubbleInfo = { label: string; bubbleType: BubbleType };

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

function hasValue(v: unknown) {
  return v !== undefined && v !== null && String(v).trim() !== "";
}

export function PurchaseInfoHeader({
  date,
  invoiceNumber,
  vendor,
  email,
  mobile,
  statusBubbles,
  edited,
  lastEditedAt,
  outstanding,
  dueDate,
  total,
  isPaid,
  className = "",
  statusRowEndSlot = null,
  prepaidRowLabels,
  purchaseLabels: t,
}: {
  /** Purchase callers opt in; all other callers retain their original output. */
  purchaseLabels?: (key: ParseKeys) => string;
  /** Prepaid row copy is independent of purchase status semantics. */
  prepaidRowLabels?: { date: string; supplier: string; outstanding: string; total: string };
  date: string;
  invoiceNumber?: string | number | null;
  vendor?: string | null;
  email?: string | null;
  mobile?: string | null;
  statusBubbles: BubbleInfo[];
  edited?: boolean;
  lastEditedAt?: string;
  outstanding?: number;
  dueDate?: string;
  total?: number;
  isPaid?: boolean;
  className?: string;
  /** Optional trailing content appended at the end of the status row (e.g. compact mobile actions). */
  statusRowEndSlot?: React.ReactNode;
}) {
  const statusLabel = (bubble: BubbleInfo) => {
    if (!t) return bubble.label;
    switch (bubble.bubbleType) {
      case "paid": return t("purchases.status.paid");
      case "partial": return t("purchases.status.partial");
      case "unpaid": return t("purchases.status.unpaid");
      case "voided": return t("purchases.status.voided");
      default: return bubble.label;
    }
  };
  const showSupplierInvoice = hasValue(invoiceNumber);
  const showVendor = hasValue(vendor);
  const showEmail = hasValue(email);
  const showMobile = hasValue(mobile);

  const showOutstanding =
    typeof outstanding === "number" &&
    !Number.isNaN(outstanding) &&
    outstanding > 0.004;
  const showDueDate = hasValue(dueDate) && !isPaid;
  const showTotal = typeof total === "number" && !Number.isNaN(total);
  const showEditedAt = hasValue(lastEditedAt);
  const showEditedFallback = !!edited && !showEditedAt;

  const rowLabelClass = `${A.label} min-h-5 flex items-start pt-0.5`;
  const rowValueClass = `min-h-5 flex items-center text-gray-700`;
  const rowValueNumClass = `${A.num} min-h-5 flex items-center text-gray-700`;

  return (
    <div className={`flex flex-col gap-3 mb-4 ${className}`}>
      {/* Top block: identity / reference info */}
      <div className="min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-[max-content_minmax(0,1fr)] gap-x-2 gap-y-1 text-[13px]">
          <div className={rowLabelClass}>{prepaidRowLabels?.date ?? (t ? t("sales.common.date") : "Date")}</div>
          <div className={rowValueNumClass}>{formatAnyDateToLocale(date)}</div>

          {showSupplierInvoice && (
            <>
              <div className={rowLabelClass}>{t ? t("purchases.common.supplierInvoice") : "Supplier Invoice No."}</div>
              <div className={`${rowValueNumClass} break-words`}>
                {invoiceNumber}
              </div>
            </>
          )}

          {showVendor && (
            <>
              <div className={rowLabelClass}>{prepaidRowLabels?.supplier ?? (t ? t("purchases.header.supplier") : "Supplier")}</div>
              <div className="min-h-5 flex flex-col justify-center">
                <div className={`${rowValueClass} break-words`}>{vendor}</div>

                {showEmail && (
                  <div className="text-xs text-gray-500 break-all">{email}</div>
                )}

                {showMobile && (
                  <div className="text-xs text-gray-500 break-all">
                    {mobile}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Second row: status + summary */}
      <div className="flex flex-wrap items-center gap-2">
        {statusBubbles.map((b, i) => (
          <StatusBubble
            key={`${t || prepaidRowLabels ? b.bubbleType : b.label}-${i}`}
            label={statusLabel(b)}
            bubbleType={b.bubbleType}
          />
        ))}

        {showEditedAt && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {t ? t("purchases.header.editedOn") : "Edited on"}
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {formatAnyDateToLocale(lastEditedAt)}
            </span>
          </span>
        )}

        {showEditedFallback && <span className={A.label}>{t ? t("purchases.header.edited") : "Edited"}</span>}

        {showOutstanding && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {prepaidRowLabels?.outstanding ?? (t ? t("sales.common.outstanding") : "Outstanding")}
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {money(outstanding)}
            </span>
          </span>
        )}

        {showDueDate && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {t ? t("purchases.common.dueShort") : "Due"}
            <span className={`font-medium text-gray-700 ${A.num}`}>
              {formatAnyDateToLocale(dueDate)}
            </span>
          </span>
        )}

        {showTotal && (
          <span className={`inline-flex items-center gap-1 ${A.label}`}>
            {prepaidRowLabels?.total ?? (t ? t("sales.common.total") : "Total")}
            <span className={`font-semibold text-gray-700 ${A.num}`}>
              {money(total)}
            </span>
          </span>
        )}

        {statusRowEndSlot}
      </div>
    </div>
  );
}